import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Send, FolderKanban, Users, ChevronRight, Hash
} from 'lucide-react';

export default function InternalChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const msgEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    api.getProjects().then(r => setProjects(r.data)).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (activeConv) {
      fetchMessages(activeConv.id);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(activeConv.id), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await api.getInternalChats();
      setConversations(res.data);
    } catch (err) { console.warn('Failed to fetch chats:', err?.message); }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await api.getInternalChatMessages(convId);
      setMessages(res.data);
    } catch (err) { console.warn('Failed to fetch messages:', err?.message); }
  };

  const handleCreateConv = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await api.createInternalChat({
        title: newTitle,
        project_id: selectedProject || null,
      });
      setConversations(prev => [res.data, ...prev]);
      setActiveConv(res.data);
      setShowNew(false);
      setNewTitle('');
      setSelectedProject('');
    } catch (err) {
      toast.error('Kunde inte skapa konversation');
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await api.sendInternalMessage(activeConv.id, { message: newMsg });
      setNewMsg('');
      fetchMessages(activeConv.id);
      fetchConversations();
    } catch (err) {
      toast.error('Kunde inte skicka meddelande');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex h-[calc(100vh-80px)] bg-background" data-testid="internal-chat-page">
      {/* Sidebar */}
      <div className="w-80 border-r border-border flex flex-col bg-card">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <MessageSquare size={16} className="text-[#E8613A]" /> Intern Chatt
          </h2>
          <Button size="sm" variant="outline" onClick={() => setShowNew(!showNew)} data-testid="new-chat-btn">
            <Plus size={14} />
          </Button>
        </div>

        {showNew && (
          <div className="p-3 border-b border-border bg-muted/30 space-y-2">
            <Input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="Titel..."
              className="text-sm h-8"
              data-testid="new-chat-title"
            />
            <select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              className="w-full text-sm h-8 rounded border border-border bg-background px-2"
              data-testid="new-chat-project"
            >
              <option value="">Inget projekt</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Button size="sm" className="w-full bg-[#E8613A] hover:bg-[#d4552f] text-white text-xs" onClick={handleCreateConv} data-testid="create-chat-btn">
              Skapa
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">Inga konversationer ännu</p>
          ) : conversations.map(conv => (
            <button
              key={conv.id}
              onClick={() => setActiveConv(conv)}
              className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors ${activeConv?.id === conv.id ? 'bg-muted' : ''}`}
              data-testid={`conv-${conv.id}`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-sm font-medium text-foreground truncate">{conv.title}</span>
                {conv.unread_count > 0 && (
                  <span className="bg-[#E8613A] text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{conv.unread_count}</span>
                )}
              </div>
              {conv.project_name && (
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Hash size={10} /> {conv.project_name}</span>
              )}
              {conv.last_message && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.last_message.sender_name}: {conv.last_message.message}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {activeConv ? (
          <>
            <div className="px-5 py-3 border-b border-border bg-card flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-foreground">{activeConv.title}</h3>
                {activeConv.project_name && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <FolderKanban size={10} /> {activeConv.project_name}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {messages.map(msg => {
                const isMe = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                    <div className={`max-w-[70%] rounded-xl px-4 py-2.5 ${isMe ? 'bg-[#E8613A] text-white' : 'bg-muted text-foreground'}`}>
                      {!isMe && <p className="text-xs font-medium mb-0.5 opacity-70">{msg.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-1 ${isMe ? 'text-white/60' : 'text-muted-foreground'}`}>{formatTime(msg.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            <div className="px-5 py-3 border-t border-border bg-card">
              <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                <Input
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  placeholder="Skriv meddelande..."
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !newMsg.trim()} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="send-msg-btn">
                  <Send size={16} />
                </Button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare size={40} className="text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-muted-foreground">Välj en konversation eller skapa en ny</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
